import { z } from "zod";

export const initiateSchema = z.object({
  date_of_issue: z.string().optional(),

  department: z
    .string()
    .min(1, "Department is required")
    .max(60, "Department should be less than 60 characters")
    .nullable(),

  department_other: z
    .string()
    .max(250, "Department (Other) should be less than 250 characters")
    .optional()
    .nullable(),

  reported_by: z
    .string()
    .min(1, "Reported By is required")
    .max(250, "Reported By should be less than 250 characters")
    .nullable(),

  ncr_type: z
    .string()
    .min(1, "NCR Type is required")
    .max(60, "NCR Type should be less than 60 characters")
    .nullable(),

  location: z
    .string()
    .min(1, "Location is required")
    .max(60, "Location should be less than 60 characters")
    .nullable(),

  category: z
    .string()
    .min(1, "Category is required")
    .max(60, "Category should be less than 60 characters")
    .nullable(),

  affected_item_component_no: z
    .string()
    .min(1, "Component No is required")
    .max(250, "Component No should be less than 250 characters")
    .nullable(),

  affected_item_revision: z
    .string({ invalid_type_error: "Revision must be a string" })
    .min(1, "Revision is required")
    .max(250, "Revision should be less than 250 characters")
    .nullable(),

  affected_item_qty: z.coerce
    .number({ invalid_type_error: "Quantity must be a number" })
    .nullable(),

  affected_item_description: z
    .string()
    .min(1, "Description is required")
    .max(250, "Description should be less than 250 characters")
    .nullable(),

  affected_item_supplier_cust_name: z
    .string()
    .min(1, "Supplier / Customer Name is required")
    .max(250, "Supplier / Customer Name should be less than 250 characters")
    .nullable(),

  affected_item_remarks: z
    .string()
    .max(250, "Remarks should be less than 250 characters")
    .optional()
    .nullable(),

  what_went_wrong: z
    .string()
    .min(1, "This field is required")
    .max(250, "What Went Wrong should be less than 250 characters")
    .nullable(),

  how_was_issue_identified: z
    .string()
    .min(1, "This field is required")
    .max(250, "How Issue Was Identified should be less than 250 characters")
    .nullable(),

  relevant_measurements_observations_specification: z
    .string()
    .max(250, "Relevant Observations should be less than 250 characters")
    .optional()
    .nullable(),

  evidence_attachments: z.any().optional().nullable(),

  severity_or_priority_level: z
    .string()
    .optional()
    .nullable(),

  responsible_team_or_person: z
    .string()
    .min(1, "Responsible Team/Person is required")
    .max(60, "Responsible Team/Person should be less than 60 characters")
    .nullable(),

  corrective_action_required: z.string().optional().nullable(),
  approving_authority: z
    .string()
    .max(60, "Approving Authority should be less than 60 characters")
    .optional()
    .nullable(),

  status: z
    .string()
    .max(60, "Status should be less than 60 characters")
    .optional()
    .nullable(),
});

export const actionSchema = z.object({
  escape_point: z
    .string()
    .max(60, "Escape Point should be less than 60 characters")
    .optional()
    .nullable(),

  escape_point_other: z
    .string()
    .max(250, "Escape Point (Other) should be less than 250 characters")
    .optional()
    .nullable(),

  implement_interim_containment_action: z
    .string()
    .max(60, "Implement Interim Containment Action should be less than 60 characters")
    .optional()
    .nullable(),

  implement_interim_containment_action_other: z
    .string()
    .max(250, "Implement Interim Containment Action (Other) should be less than 250 characters")
    .optional()
    .nullable(),

  ndrc_identification: z
    .string()
    .max(60, "NDRC Identification should be less than 60 characters")
    .optional()
    .nullable(),

  ndrc_identification_other: z
    .string()
    .max(250, "NDRC Identification (Other) should be less than 250 characters")
    .optional()
    .nullable(),

  corrective_actions_ndrc: z
    .string()
    .max(250, "Corrective Actions (NDRC) should be less than 250 characters")
    .optional()
    .nullable(),

  process_root_cause_description: z
    .string()
    .max(250, "Process Root Cause Description should be less than 250 characters")
    .optional()
    .nullable(),

  process_root_cause_identification: z
    .string()
    .max(60, "Process Root Cause Identification should be less than 60 characters")
    .optional()
    .nullable(),

  process_root_cause_identification_other: z
    .string()
    .max(250, "Process Root Cause Identification (Other) should be less than 250 characters")
    .optional()
    .nullable(),

  corrective_actions_process_root_cause: z
    .string()
    .max(250, "Corrective Actions (PRC) should be less than 250 characters")
    .optional()
    .nullable(),

  system_root_cause_identification: z
    .string()
    .max(60, "System Root Cause Identification should be less than 60 characters")
    .optional()
    .nullable(),

  system_root_cause_identification_other: z
    .string()
    .max(250, "System Root Cause Identification (Other) should be less than 250 characters")
    .optional()
    .nullable(),

  corrective_actions_system_root_cause: z
    .string()
    .max(250, "Corrective Actions (SRC) should be less than 250 characters")
    .optional()
    .nullable(),

  preventive_action_plan: z
    .string()
    .max(250, "Preventive Action Plan should be less than 250 characters")
    .optional()
    .nullable(),

  preventive_action_implementation_timeline: z
    .coerce.date({ invalid_type_error: "Timeline must be a valid date" })
    .optional()
    .nullable(),

  action_attachments: z.any().optional().nullable(),
});

export const reviewSchema = z.object({
  corrective_action_review_completed: z
    .enum(["Yes", "No"])
    .optional()
    .nullable(),

  is_corrective_action_review_effective_resolving_issue: z
    .enum(["Yes", "No"])
    .optional()
    .nullable(),

  corrective_action_review_feedback: z
    .string()
    .max(250, "Corrective Action Feedback should be less than 250 characters")
    .optional()
    .nullable(),

  corrective_action_review_verification_evidence: z
    .any()
    .optional()
    .nullable(),

  preventive_action_review_implemented: z
    .enum(["Yes", "No"])
    .optional()
    .nullable(),

  preventive_action_review_sufficient_prevent_recurrence: z
    .enum(["Yes", "No"])
    .optional()
    .nullable(),

  preventive_action_review_feedback: z
    .string()
    .max(250, "Preventive Action Feedback should be less than 250 characters")
    .optional()
    .nullable(),

  preventive_action_review_verification_evidence: z
    .any()
    .optional()
    .nullable(),

  is_reroute: z
    .boolean()
    .optional()
    .nullable(),
});

export const closureSchema = z.object({
  is_ncr_ready_for_closure: z
    .enum(["Yes", "No"])
    .optional()
    .nullable(),

  comments_for_closure: z
    .string()
    .max(250, "Comments for closure should be less than 250 characters")
    .optional()
    .nullable(),

  is_reroute: z
    .boolean()
    .optional()
    .nullable(),
});